import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function About() {
  const organizationSchema = generateOrganizationSchema();
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'About', url: '/about' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [organizationSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'About InteCare Westminster CA - Professional Medical Transportation Team | HIPAA Compliant',
    description: 'Learn about InteCare, Westminster CA\'s trusted non-emergency medical transportation provider since 2014. Professional drivers, HIPAA compliant, 24/7 service with Integrity and Care throughout Orange County.',
    keywords: 'about InteCare, medical transportation Westminster, NEMT company Orange County, professional medical transport, HIPAA compliant transport, certified medical drivers',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/124edb4340d36fa2d4dac319d5fc2fa7.jpg')`
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              About InteCare
            </h1>
            <p className="text-xl text-white leading-relaxed">
              Providing compassionate, reliable, and professional non-emergency medical transportation services throughout Orange County CA and surrounding areas since 2014.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Our Mission
              </h2>
              <p className="text-gray-700 leading-relaxed">
                To provide safe, reliable, and compassionate non-emergency medical transportation services with Integrity and Care that enhance the quality of life for our patients. We are committed to ensuring every individual receives dignified, comfortable, and timely transportation to their medical appointments and healthcare facilities.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Our Vision
              </h2>
              <p className="text-gray-700 leading-relaxed">
                To be the most trusted and preferred non-emergency medical transportation provider in Southern California, recognized for our exceptional service delivered with Integrity and Care, professional excellence, and unwavering commitment to patient safety and comfort. We strive to set the standard for excellence in medical transportation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Our Story
            </h2>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <p className="text-gray-700 leading-relaxed mb-6">
                InteCare was founded with a simple yet powerful vision: to provide reliable, compassionate medical transportation services with Integrity and Care to those who need it most. We recognized that many individuals face challenges accessing essential healthcare services due to transportation barriers, and we set out to bridge that gap.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Based in Westminster, California, we have grown from a small operation to a trusted provider serving the entire Orange County area. Our team consists of professionally trained drivers and medical transport specialists who are dedicated to ensuring every patient receives the highest level of care and attention, always operating with integrity and genuine compassion.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Today, InteCare continues to uphold the values that founded our company: compassion, reliability, professionalism, and an unwavering commitment to patient safety. Our tagline "with Integrity and Care" isn't just words—it's the foundation of everything we do. We are proud to serve our community and help individuals access the healthcare services they need with dignity and comfort.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Safety First</h3>
              <p className="text-gray-600">
                Patient safety is our top priority. Our vehicles are regularly maintained and our drivers are thoroughly trained in safe transport procedures.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-3-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Compassion</h3>
              <p className="text-gray-600">
                We treat every patient with dignity, respect, and genuine care, understanding the unique needs of each individual we serve.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Reliability</h3>
              <p className="text-gray-600">
                We understand the importance of timely medical appointments. Our punctual service ensures you arrive on time, every time.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Excellence</h3>
              <p className="text-gray-600">
                We continuously strive for excellence in every aspect of our service, from vehicle maintenance to customer care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Why Choose InteCare?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-star-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Professional Drivers</h3>
                  <p className="text-gray-600">
                    All our drivers are professionally trained, background-checked, and certified in patient care and safe transport procedures.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-car-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Modern Fleet</h3>
                  <p className="text-gray-600">
                    Our vehicles are equipped with the latest safety features and medical equipment to ensure comfortable and secure transportation.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">HIPAA Compliant</h3>
                  <p className="text-gray-600">
                    We maintain strict confidentiality and comply with all HIPAA regulations to protect your privacy and medical information.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-24-hours-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">24/7 Availability</h3>
                  <p className="text-gray-600">
                    We offer round-the-clock emergency transport services and flexible scheduling to meet your healthcare needs.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Insurance Accepted</h3>
                  <p className="text-gray-600">
                    We work with most major insurance providers and offer affordable rates for private pay patients.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Local Expertise</h3>
                  <p className="text-gray-600">
                    With deep knowledge of Orange County, we ensure efficient routes and timely arrivals to all medical facilities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Our Dedicated Team
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Our team consists of compassionate professionals who are committed to providing the highest quality medical transportation services.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-teal-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-line text-6xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Certified Drivers</h3>
              <p className="text-gray-600">
                Professionally trained and certified in patient care, CPR, and first aid with extensive experience in medical transportation.
              </p>
            </div>

            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-6xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Support Staff</h3>
              <p className="text-gray-600">
                Friendly and knowledgeable customer service team available to assist with scheduling, questions, and special requests.
              </p>
            </div>

            <div className="text-center">
              <div className="w-32 h-32 bg-gradient-to-br from-teal-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-stethoscope-line text-6xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Medical Coordinators</h3>
              <p className="text-gray-600">
                Experienced coordinators who work closely with healthcare facilities to ensure seamless transportation logistics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Ready to Experience Quality Care?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied patients who trust InteCare for their medical transportation needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
